import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo } from 'react';
import { useMatch } from 'react-router';
import {
  buildOrgScope,
  type OrgScope,
  OrgScopeContext,
  readLastOrgSlug,
  useOrganizations,
  useOwnMemberships,
} from '@/entities/org';
import { useOwnGrants, useWorkspaces } from '@/entities/workspace';

/**
 * Loads what decides the signed-in person's view (their orgs, roles, visible workspaces and grants, all
 * through RLS) and resolves the active org from the URL. Mounted once, above the shell, so the sidebar and
 * the pages read the same scope. (`RequireAuth` drops the query cache when the user changes.)
 */
export function OrgScopeProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const qc = useQueryClient();
  const organizations = useOrganizations();
  const memberships = useOwnMemberships(userId);
  const workspaces = useWorkspaces();
  const grants = useOwnGrants(userId);
  const slugInUrl = useMatch('/o/:orgSlug/*')?.params.orgSlug ?? null;

  const queries = [organizations, memberships, workspaces, grants];
  const failed = queries.find((q) => q.isError);
  const pending = queries.some((q) => q.isPending);
  const failure = failed?.error ?? null;

  const scope = useMemo<OrgScope>(() => {
    if (failure)
      return {
        status: 'error',
        error: failure,
        retry: () => void qc.invalidateQueries({ queryKey: ['org-scope'] }),
      };
    if (pending || !organizations.data || !memberships.data || !workspaces.data || !grants.data)
      return { status: 'loading' };
    return {
      status: 'ready',
      ...buildOrgScope({
        organizations: organizations.data,
        memberships: memberships.data,
        workspaces: workspaces.data,
        grants: grants.data,
        slugInUrl,
        lastSlug: readLastOrgSlug(),
      }),
    };
  }, [
    failure,
    pending,
    organizations.data,
    memberships.data,
    workspaces.data,
    grants.data,
    slugInUrl,
    qc,
  ]);

  return <OrgScopeContext.Provider value={scope}>{children}</OrgScopeContext.Provider>;
}
